// src/components/grammar/GrammarSidebar.jsx
import { useEffect, useRef, memo } from 'react';
import { BookOpen, X } from 'lucide-react';
import PropTypes from 'prop-types';

function GrammarSidebar({ isOpen, onClose, modules, currentModuleId, onSelect }) {
  const sidebarRef = useRef(null);

  // Focus trap: Usando refs en lugar de querySelector
  useEffect(() => {
    if (isOpen) {
      // Buscamos el primer botón dentro del aside para darle focus
      const focusableElements = sidebarRef.current?.querySelectorAll('button');
      if (focusableElements && focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [isOpen]);

  // Cerrar con ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Overlay con transición suave */}
      <div 
        className={`absolute inset-0 bg-slate-900/60 z-30 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        ref={sidebarRef} // ✅ Referencia para el focus
        role="dialog"
        aria-modal="true"
        aria-label="Menú de temario de gramática"
        className={`absolute top-0 right-0 h-full w-80 bg-white shadow-2xl z-40 transform transition-transform duration-500 ease-out rounded-l-[2.5rem] flex flex-col sm:w-full md:w-80 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <header className="p-6 border-b flex justify-between items-center bg-white rounded-tl-[2.5rem]">
          <h2 className="font-black text-xl flex items-center gap-2 text-slate-800">
            <BookOpen className="text-indigo-600" size={24} aria-hidden="true" /> 
            Temario
          </h2>
          <button 
            onClick={onClose} 
            aria-label="Cerrar menú de temario"
            className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 text-slate-500 transition-all active:scale-95"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <nav className="overflow-y-auto p-4 space-y-3 flex-1 bg-slate-50">
          {Object.values(modules).map((mod) => (
            <button 
              key={mod.id}
              onClick={() => onSelect(mod)}
              aria-current={mod.id === currentModuleId ? 'page' : undefined}
              className={`w-full p-4 rounded-2xl text-left border-2 transition-all focus:outline-none focus:ring-4 focus:ring-indigo-200 ${
                mod.id === currentModuleId 
                ? 'bg-white border-indigo-500 text-indigo-700 shadow-md scale-[1.02]' 
                : 'bg-white border-transparent text-slate-500 hover:border-indigo-100 hover:text-indigo-600'
              }`}
            >
              <span className="text-[10px] font-black uppercase opacity-50 block mb-1">{mod.id}</span>
              <span className="font-bold text-sm block leading-tight">{mod.name}</span>
              {mod.id === currentModuleId && (
                <span className="sr-only"> (Módulo actual)</span>
              )}
            </button>
          ))}
        </nav>
      </aside>
    </>
  );
}

GrammarSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  modules: PropTypes.object.isRequired,
  currentModuleId: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired
};

// ✅ Usamos memo directamente desde la importación
export default memo(GrammarSidebar);